/**
 * Filter Registry - Central plugin system for all filters
 *
 * This registry manages all available filters in the application.
 * Adding a new filter is as simple as registering it here.
 */

import { Filter, FilterType, FilterCategory, UserTier, TIER_HIERARCHY } from '../types/filters';

/**
 * Central filter registry - singleton pattern
 */
class FilterRegistry {
  private filters: Map<string, Filter> = new Map();
  private initialized: boolean = false;

  /**
   * Register a new filter
   * @throws Error if filter ID already exists
   */
  register<T extends FilterType>(filter: Filter<T>): void {
    if (this.filters.has(filter.id)) {
      throw new Error(`Filter with ID "${filter.id}" is already registered`);
    }

    // Validate filter definition
    this.validateFilter(filter as unknown as Filter);

    this.filters.set(filter.id, filter as unknown as Filter);
  }

  /**
   * Register multiple filters at once
   */
  registerMany(filters: Filter[]): void {
    filters.forEach((f) => this.register(f));
  }

  /**
   * Get filter by ID
   */
  get(id: string): Filter | undefined {
    return this.filters.get(id);
  }

  /**
   * Get all registered filters
   */
  getAll(): Filter[] {
    return Array.from(this.filters.values());
  }

  /**
   * Get filters by category
   */
  getByCategory(category: FilterCategory): Filter[] {
    return this.getAll().filter((f) => f.category === category);
  }

  /**
   * Get filters available for a specific tier
   * When isLaunchPromo is true, all filters are available regardless of tier.
   */
  getByTier(tier: UserTier, isLaunchPromo?: boolean): Filter[] {
    if (isLaunchPromo) return this.getAll();
    return this.getAll().filter((f) => {
      return TIER_HIERARCHY[tier] >= TIER_HIERARCHY[f.minTier];
    });
  }

  /**
   * Get locked filters for a specific tier (for upgrade prompts)
   * When isLaunchPromo is true, no filters are locked.
   */
  getLockedForTier(tier: UserTier, isLaunchPromo?: boolean): Filter[] {
    if (isLaunchPromo) return [];
    return this.getAll().filter((f) => {
      return TIER_HIERARCHY[tier] < TIER_HIERARCHY[f.minTier];
    });
  }

  /**
   * Get only enabled filters
   */
  getEnabled(): Filter[] {
    return this.getAll().filter((f) => f.enabled);
  }

  /**
   * Get filters sorted by popularity
   */
  getByPopularity(): Filter[] {
    return [...this.getAll()].sort((a, b) => a.popularityRank - b.popularityRank);
  }

  /**
   * Get premium filters only
   */
  getPremiumFilters(): Filter[] {
    return this.getAll().filter((f) => f.premiumFeature);
  }

  /**
   * Check if a filter exists
   */
  has(id: string): boolean {
    return this.filters.has(id);
  }

  /**
   * Get filter count
   */
  count(): number {
    return this.filters.size;
  }

  /**
   * Clear all filters (mainly for testing)
   */
  clear(): void {
    this.filters.clear();
    this.initialized = false;
  }

  /**
   * Check if registry has been initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Mark registry as initialized
   */
  setInitialized(): void {
    this.initialized = true;
  }

  /**
   * Validate filter definition
   * @throws Error if validation fails
   */
  private validateFilter(filter: Filter): void {
    // Check required fields
    if (!filter.id || typeof filter.id !== 'string') {
      throw new Error('Filter must have a valid ID');
    }
    if (!filter.name || typeof filter.name !== 'string') {
      throw new Error(`Filter "${filter.id}" must have a name`);
    }
    if (!filter.type) {
      throw new Error(`Filter "${filter.id}" must have a type`);
    }
    if (!filter.category) {
      throw new Error(`Filter "${filter.id}" must have a category`);
    }
    if (!filter.dataPath || typeof filter.dataPath !== 'string') {
      throw new Error(`Filter "${filter.id}" must have a dataPath`);
    }
    if (filter.defaultValue === undefined) {
      throw new Error(`Filter "${filter.id}" must have a defaultValue`);
    }
    if (!filter.config) {
      throw new Error(`Filter "${filter.id}" must have a config object`);
    }

    // Validate popularity rank
    if (typeof filter.popularityRank !== 'number' || filter.popularityRank < 0) {
      throw new Error(`Filter "${filter.id}" must have a valid popularityRank >= 0`);
    }
  }

  /**
   * Get statistics about registered filters
   */
  getStats(): {
    total: number;
    byCategory: Record<FilterCategory, number>;
    byTier: Record<UserTier, number>;
    enabled: number;
    premium: number;
  } {
    const all = this.getAll();

    const byCategory = all.reduce((acc, filter) => {
      acc[filter.category] = (acc[filter.category] || 0) + 1;
      return acc;
    }, {} as Record<FilterCategory, number>);

    const byTier = all.reduce((acc, filter) => {
      acc[filter.minTier] = (acc[filter.minTier] || 0) + 1;
      return acc;
    }, {} as Record<UserTier, number>);

    return {
      total: all.length,
      byCategory,
      byTier,
      enabled: this.getEnabled().length,
      premium: this.getPremiumFilters().length
    };
  }
}

/**
 * Singleton instance
 */
export const filterRegistry = new FilterRegistry();

/**
 * Helper function to get filter definitions for a user's tier
 */
export function getFiltersForUser(tier: UserTier): {
  available: Filter[];
  locked: Filter[];
} {
  return {
    available: filterRegistry.getByTier(tier).filter((f) => f.enabled),
    locked: filterRegistry.getLockedForTier(tier).filter((f) => f.enabled)
  };
}

/**
 * Helper function to check if user has access to a filter
 */
export function hasFilterAccess(filterId: string, tier: UserTier): boolean {
  const filter = filterRegistry.get(filterId);
  if (!filter) return false;
  if (!filter.enabled) return false;

  return TIER_HIERARCHY[tier] >= TIER_HIERARCHY[filter.minTier];
}
